/**
 * The Academics → Batches list page's filter + sort declaration — a consumer of
 * the generic filter toolkit (`components/filters`), mirroring
 * `studentsFilterConfig` and `usersFilterConfig`.
 *
 * Facets exposed (every one is read by the batches backend — see
 * `apps/api/src/routes/batches.ts` GET '/'):
 *   - search      → `q`            debounced free text over the batch name.
 *   - status      → `status`       multi-select over draft/active/completed/
 *                                  archived, default ['active'] (the page's prior
 *                                  default chip). allEncoding 'all': the backend
 *                                  treats an omitted status as "no constraint",
 *                                  so sending the literal `all` for the "All"
 *                                  choice is harmless and keeps the encoding
 *                                  aligned with the other list pages.
 *   - mode        → `mode`         multi-select over online/offline/hybrid,
 *                                  omit encoding (no mode param = all modes).
 *   - courseId    → `courseId`     lookup over /api/courses. That endpoint has no
 *                                  `q` search and no pagination (it returns the
 *                                  full active-course list), so the lookup fetches
 *                                  the list once and filters client-side by name —
 *                                  courses are few. resolveLabel reads /:id.
 *   - teacherId   → `teacherId`    lookup over /api/teachers (server-side `q`).
 *   - nalimId     → `nalimId`      lookup over /api/managers (server-side `q`).
 *   - start       → `startFrom`/`startTo` date range over the batch start date.
 *
 * Sorts map to the backend's allow-listed `sort` fields
 * (startDate|name|createdAt). The default (first entry) is "Start date (newest)"
 * = startDate:desc. NOTE: the backend *fallback* is startDate:asc, but the
 * toolkit always sends an explicit `sort`, so this default flips the visible
 * order to newest-start-first — intentional per the migration spec.
 *
 * Sortable column headers: Name (name) and Start (startDate). The createdAt
 * sorts are dropdown-only (the table has no "Added" column).
 */

import { z } from 'zod';
import { api } from '../../lib/api';
import type { CourseSummary } from './types';
import type { TeacherRecord, ManagerRecord } from '../people/types';
import type { ListFilterConfig, SortColumnBinding } from '../filters';

const LOOKUP_PAGE_SIZE = 10;

/**
 * Course options for the Course lookup. `/api/courses` returns the full active
 * course list with neither a `q` search nor pagination, so we fetch once and
 * filter client-side by name (courses number in the tens, not thousands). The
 * result is capped to keep the picker list short and predictable.
 */
const loadCourseOptions = async (
  query: string,
): Promise<{ value: string; label: string }[]> => {
  const res = await api.list<CourseSummary>('/api/courses');
  const term = query.trim().toLowerCase();
  const matches = term
    ? res.items.filter((course) => course.name.toLowerCase().includes(term))
    : res.items;
  return matches.slice(0, LOOKUP_PAGE_SIZE).map((course) => ({
    value: course.id,
    label: course.name,
  }));
};

/** Resolve a course id to its name (hydrates a pill from a shared link). */
const resolveCourseLabel = async (id: string): Promise<string | null> => {
  const course = await api
    .crud<CourseSummary, never, never>('/api/courses')
    .get(id);
  return course.name ?? null;
};

/** Search teachers by name / employee id → option rows for the Teacher lookup. */
const loadTeacherOptions = async (
  query: string,
): Promise<{ value: string; label: string }[]> => {
  const res = await api.list<TeacherRecord>('/api/teachers', {
    q: query || undefined,
    pageSize: LOOKUP_PAGE_SIZE,
  });
  return res.items.map((teacher) => ({ value: teacher.id, label: teacher.name }));
};

/** Resolve a teacher id to their name. */
const resolveTeacherLabel = async (id: string): Promise<string | null> => {
  const teacher = await api
    .crud<TeacherRecord, never, never>('/api/teachers')
    .get(id);
  return teacher.name ?? null;
};

/** Search managers (nalim) by name / employee id → option rows for the lookup. */
const loadNalimOptions = async (
  query: string,
): Promise<{ value: string; label: string }[]> => {
  const res = await api.list<ManagerRecord>('/api/managers', {
    q: query || undefined,
    pageSize: LOOKUP_PAGE_SIZE,
  });
  return res.items.map((manager) => ({ value: manager.id, label: manager.name }));
};

/** Resolve a manager (nalim) id to their name. */
const resolveNalimLabel = async (id: string): Promise<string | null> => {
  const manager = await api
    .crud<ManagerRecord, never, never>('/api/managers')
    .get(id);
  return manager.name ?? null;
};

export const batchesFilterConfig: ListFilterConfig = {
  facets: [
    {
      kind: 'search',
      key: 'q',
      label: 'Search',
      placeholder: 'Search by batch name',
    },
    {
      kind: 'multi-select',
      key: 'status',
      label: 'Status',
      group: 'filters',
      // Prior page default was the "Active" chip. `defaultValue: ['active']`
      // reproduces that. allEncoding 'all' is harmless (the backend reads an
      // omitted status as no-constraint) but keeps encoding uniform across the
      // list pages.
      defaultValue: ['active'],
      allEncoding: 'all',
      options: [
        { value: 'draft', label: 'Draft' },
        { value: 'active', label: 'Active' },
        { value: 'completed', label: 'Completed' },
        { value: 'archived', label: 'Archived' },
      ],
    },
    {
      kind: 'multi-select',
      key: 'mode',
      label: 'Mode',
      group: 'filters',
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'online', label: 'Online' },
        { value: 'offline', label: 'Offline' },
        { value: 'hybrid', label: 'Hybrid' },
      ],
    },
    {
      kind: 'lookup',
      key: 'courseId',
      label: 'Course',
      group: 'filters',
      searchNoun: 'courses',
      loadOptions: loadCourseOptions,
      resolveLabel: resolveCourseLabel,
    },
    {
      kind: 'lookup',
      key: 'teacherId',
      label: 'Teacher',
      group: 'filters',
      searchNoun: 'teachers',
      loadOptions: loadTeacherOptions,
      resolveLabel: resolveTeacherLabel,
    },
    {
      kind: 'lookup',
      key: 'nalimId',
      label: 'Nalim',
      group: 'filters',
      searchNoun: 'nalims',
      loadOptions: loadNalimOptions,
      resolveLabel: resolveNalimLabel,
    },
    {
      kind: 'date-range',
      key: 'start',
      label: 'Start date',
      group: 'filters',
      params: { from: 'startFrom', to: 'startTo' },
    },
  ],
  sorts: [
    { value: 'startDate:desc', label: 'Start date (newest)' },
    // Registered so the Start sortable header can toggle to ascending; an
    // unregistered `field:dir` would silently fall back to the default sort.
    { value: 'startDate:asc', label: 'Start date (oldest)' },
    { value: 'name:asc', label: 'Name A–Z' },
    { value: 'name:desc', label: 'Name Z–A' },
    { value: 'createdAt:desc', label: 'Recently added' },
  ],
  resultNoun: ['batch', 'batches'],
};

/**
 * Column-header sort bindings, synced to the same `sort` param. The Name header
 * sorts on `name`; the Start header sorts on `startDate`. Both fields are on the
 * backend allow-list; `createdAt` is dropdown-only (no matching table column).
 */
export const BATCHES_SORT_COLUMNS: Record<'name' | 'startDate', SortColumnBinding> = {
  name: { field: 'name', firstClickDir: 'asc' },
  startDate: { field: 'startDate', firstClickDir: 'desc' },
};

/**
 * URL search-param contract for the Batches list, used as the route's
 * `validateSearch`. The filter toolkit is the single source of truth for these
 * values; this schema validates the raw URL at the route boundary (everything
 * optional, since a bare `/academics/batches` opens on the configured
 * defaults).
 *
 * EVERY param key the config can emit must appear here — `z.object().parse`
 * strips unknown keys, so a missing key would be silently dropped on the next
 * read. In particular `start` is the date-range facet's PRESET key (e.g.
 * `last30`), distinct from the concrete custom bounds `startFrom`/`startTo`;
 * omitting it would stop a preset choice from sticking. Kept beside the config
 * (not in the route) so this boundary is unit-testable without loading the
 * route's React/router side effects.
 */
export const batchesSearchSchema = z.object({
  q: z.string().optional(),
  status: z.string().optional(),
  mode: z.string().optional(),
  courseId: z.string().optional(),
  teacherId: z.string().optional(),
  nalimId: z.string().optional(),
  start: z.string().optional(),
  startFrom: z.string().optional(),
  startTo: z.string().optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});
